import { Property } from '../types/property';
import { Heart, MapPin, Bed, Bath, Car, Square } from 'lucide-react';
import { usePropertyStore } from '../store/usePropertyStore';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

interface PropertyCardProps {
  property: Property;
}

export default function PropertyCard({ property }: PropertyCardProps) {
  const { favorites, toggleFavorite } = usePropertyStore();
  const isFavorite = favorites.includes(property.id);
  const location = useLocation();
  // Leva a URL atual (com o filtro, que mora na query string) junto para o
  // detalhe, para que o botao "Voltar" de lá saiba para onde retornar.
  const origem = `${location.pathname}${location.search}`;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0
    }).format(price);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="group relative bg-white rounded-sm overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500"
    >
      <Link to={`/imovel/${property.id}`} state={{ from: origem }} className="block">
        <div className="relative h-64 overflow-hidden bg-gray-200">
          <img
            src={property.images[0]}
            alt={property.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          />
          <div className="absolute top-4 right-4">
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleFavorite(property.id);
              }}
              className="p-2 bg-white/90 rounded-full hover:bg-white transition-colors duration-300"
            >
              <Heart
                className={`w-5 h-5 ${
                  isFavorite ? 'fill-red-500 text-red-500' : 'text-gray-600'
                }`}
              />
            </button>
          </div>
        </div>
        
        <div className="p-6">
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-sm text-gray-500 mb-1">COD. {property.code}</p>
              <div className="space-y-1">
                {property.salePrice && (
                  <p className="text-2xl font-semibold text-forest-900">
                    Venda: {formatPrice(property.salePrice)}
                  </p>
                )}
                {property.rentPrice && (
                  <p className="text-2xl font-semibold text-forest-900">
                    Locação: {formatPrice(property.rentPrice)}
                  </p>
                )}
                {!property.salePrice && !property.rentPrice && (
                  <p className="text-2xl font-semibold text-forest-900">
                    {formatPrice(property.price)}
                  </p>
                )}
              </div>
              {/* Valores de Condomínio e IPTU */}
              <div className="flex flex-col gap-1 mt-2 text-xs text-gray-600">
                {property.condominio && property.condominio > 0 && (
                  <span>
                    Cond.: <span className="font-medium text-forest-800">{formatPrice(property.condominio)}</span>
                  </span>
                )}
                {property.iptu && property.iptu > 0 && (
                  <span>
                    IPTU: <span className="font-medium text-forest-800">{formatPrice(property.iptu)}</span>
                    {property.tipoiptu && <span className="text-gray-500">/{property.tipoiptu}</span>}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center text-gray-600 mb-3">
            <MapPin className="w-4 h-4 mr-2" />
            <span className="text-sm">
              {property.location.neighborhood} / {property.location.city}
            </span>
          </div>
          
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div className="flex items-center text-gray-600">
              <Square className="w-4 h-4 mr-1" />
              <span className="text-sm">{property.features.area}m²</span>
            </div>
            <div className="flex items-center text-gray-600">
              <Bed className="w-4 h-4 mr-1" />
              <span className="text-sm">{property.features.bedrooms}</span>
            </div>
            <div className="flex items-center text-gray-600">
              <Bath className="w-4 h-4 mr-1" />
              <span className="text-sm">{property.features.bathrooms}</span>
            </div>
            <div className="flex items-center text-gray-600">
              <Car className="w-4 h-4 mr-1" />
              <span className="text-sm">{property.features.garageSpaces}</span>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
